import { useCallback, useEffect, useState } from "react";

const API_BASE_URL = "";
const POPUP_ENDPOINT = `${API_BASE_URL}/api/popups?populate=*&sort=baslangic_tarihi:desc`;

const pickBestFormatUrl = (formats) => {
  if (!formats) return null;
  return (
    formats.large?.url ||
    formats.medium?.url ||
    formats.small?.url ||
    formats.thumbnail?.url ||
    null
  );
};

const resolveUrl = (raw) => {
  if (!raw) return null;
  return raw.startsWith("http") ? raw : `${API_BASE_URL}${raw}`;
};

const extractMediaUrl = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    return resolveUrl(value);
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const url = extractMediaUrl(item);
      if (url) return url;
    }
  }

  if (value.url) {
    const best = pickBestFormatUrl(value.formats) || value.url;
    return resolveUrl(best);
  }

  if (value.data) {
    if (Array.isArray(value.data) && value.data.length > 0) {
      return extractMediaUrl(value.data[0]);
    }
    if (value.data?.attributes?.url) {
      const best =
        pickBestFormatUrl(value.data.attributes.formats) ||
        value.data.attributes.url;
      return resolveUrl(best);
    }
  }

  if (value.attributes?.url) {
    const best = pickBestFormatUrl(value.attributes.formats) || value.attributes.url;
    return resolveUrl(best);
  }

  return null;
};

const normalizeItem = (item) => {
  const data = item?.attributes ? { id: item.id, ...item.attributes } : item;
  const rawUrl = data?.url || null;
  const imageUrl =
    extractMediaUrl(data?.gorsel) ||
    extractMediaUrl(data?.resim) ||
    extractMediaUrl(data?.foto) ||
    extractMediaUrl(data?.fotograf) ||
    extractMediaUrl(data?.image) ||
    extractMediaUrl(data?.gorsel_url) ||
    extractMediaUrl(data) ||
    resolveUrl(rawUrl);

  return {
    id: data?.id,
    documentId: data?.documentId,
    adi: data?.adi,
    icerik: data?.icerik,
    url: rawUrl,
    imageUrl,
    baslangic_tarihi: data?.baslangic_tarihi,
    bitis_tarihi: data?.bitis_tarihi,
    createdAt: data?.createdAt,
    updatedAt: data?.updatedAt,
    publishedAt: data?.publishedAt,
    locale: data?.locale,
  };
};

const isWithinRange = (item, now = new Date()) => {
  if (!item) return false;
  const start = item.baslangic_tarihi ? new Date(item.baslangic_tarihi) : null;
  const end = item.bitis_tarihi ? new Date(item.bitis_tarihi) : null;
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
};

export const usePopup = (options = {}) => {
  const { initialLoad = true } = options;
  const [popup, setPopup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPopup = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(POPUP_ENDPOINT);
      if (!res.ok) throw new Error("Pop-up alınamadı");
      const json = await res.json();
      const items = Array.isArray(json?.data)
        ? json.data.map(normalizeItem)
        : [];
      const now = new Date();
      const active = items.find((item) => isWithinRange(item, now)) || null;
      setPopup(active);
    } catch (err) {
      setError(err?.message || "Pop-up alınamadı");
      setPopup(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialLoad) fetchPopup();
  }, [initialLoad, fetchPopup]);

  return {
    popup,
    loading,
    error,
    refetch: fetchPopup,
  };
};
